'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  clientMatches,
  HOURS_DAY_PANEL,
  HOURS_WEEK_BOOKING,
  HOURS_WEEK_DAYS,
  HOURS_WEEKS,
  weekdayIndexOf,
} from '../demo-data'
import { useVisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, Field, fieldClasses, FieldLabel, HeaderStat } from './app-screen'
import styles from './hours-visual.module.css'
import { useScenario } from './use-scenario'

const WEEKDAYS = HOURS_WEEK_DAYS

// 3 weeks x 5 weekdays. `null` = no hours booked (empty cell). Day 16 is the target cell — its
// static entry here (`null`) is the pre-booking state; the scenario below fills it in dynamically.
const DAYS: readonly [day: number, hours: string | null][] = HOURS_WEEKS.flat().map((day) => {
  const worked: Record<number, string> = {
    6: '8:00',
    7: '8:00',
    8: '8:00',
    9: '6:00',
    10: '8:00',
    13: '8:00',
    14: '8:00',
  }
  return [day, worked[day] ?? null]
})

// The client the day is booked on, and the activity booked — both from the shared demo data so the
// name matches the client dossier visuals and the activity matches the planning boards.
const BOOKING_CLIENT = client(HOURS_WEEK_BOOKING.nr)
/** What the client field turns up while it is being typed — the booked client and its namesake. */
const LOOKUP = clientMatches(HOURS_DAY_PANEL.query).slice(0, 2)
const BOOKING_ACTIVITY = HOURS_WEEK_BOOKING.activity

const TARGET_DAY = HOURS_WEEK_BOOKING.day
const BOOKED_MINUTES = Number(HOURS_WEEK_BOOKING.hours.split(':')[0]) * 60
// Both totals are summed from the grid, so the header can never disagree with the cells below it.
const BASE_MINUTES = DAYS.reduce((sum, [, hours]) => sum + (hours ? Number(hours.split(':')[0]) * 60 : 0), 0)
const formatTotal = (minutes: number) => `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`
const TOTAL_BASE = formatTotal(BASE_MINUTES)
const TOTAL_BOOKED = formatTotal(BASE_MINUTES + BOOKED_MINUTES)

/**
 * Booking an hour, at the pace of someone doing it.
 *
 * Standstill is wanted twice here and nowhere else: while the dialog is open, because it has to be
 * read, and on "Geboekt", because that is the payoff. Everything between those is a hand moving,
 * and moves at a hand's speed. The old loop spread the same story over twelve equal beats, which
 * put its longest pauses in the wrong places — including several on an empty calendar before
 * anything had happened.
 */
const BEAT_HOLDS = [
  1600, // 0  the week booked, the total at 62:00 — the resting frame and the story's end
  700, //  1  the week as it was, with the target day ringed. Clearing and ringing happen together:
  //          dwelling on a blank grid first was time spent showing nothing.
  1400, // 2  the dialog opens — held, because it is there to be read
  800, //  3  the client name types
  700, //  4  the match is picked from the dropdown
  700, //  5  activity and amount fill in
  1300, // 6  Geboekt — held; this is the payoff
  1500, // 7  the dialog clears and the booked hours light up — held long enough to be watched
] as const

const REST_BEAT = 0

const RING_FROM = 1
const MODAL_FROM = 2
const MODAL_TO = 7
const TYPING_AT = 3
const DROPDOWN_AT = 4
const KLANT_CHOSEN_AT = 4
const ACTIVITEIT_AT = 5
const AANTAL_AT = 5
const BOOKED_AT = 6
const FILLED_FROM = 7

/** The booked hours light up as the dialog clears, so the eye lands on what just changed rather
 *  than searching a grid of fifteen cells for the one that gained an entry. */
const FLASH_AT = 7

/** Resolves `beat` (possibly SSR/reduced-motion `null`) to the beat used to derive card state. */
function effectiveBeat(beat: number | null): number {
  return beat ?? REST_BEAT
}

function klantValue(beat: number): string {
  if (beat >= KLANT_CHOSEN_AT) return BOOKING_CLIENT.name
  if (beat >= TYPING_AT) return BOOKING_CLIENT.name.slice(0, 4)
  return ''
}

/**
 * Modules card 2 — a week calendar behind an "Uren toevoegen" modal: day 16 starts empty, gets
 * selected, a client is looked up via autocomplete, the booking fields fill in, and the booking
 * lands on the calendar with the header total ticking up. Driven by `useScenario`'s 15-beat loop;
 * `beat === null` (SSR, no-JS, reduced motion) renders the resolved rest state: modal closed, day
 * 16 booked.
 */
export function HoursVisual({ className }: { className?: string }) {
  const { t, fmt } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT })
  const effBeat = effectiveBeat(beat)

  const ringed = effBeat >= RING_FROM && effBeat < FILLED_FROM
  const modalOpen = effBeat >= MODAL_FROM && effBeat < MODAL_TO
  const dropdownOpen = effBeat === DROPDOWN_AT
  const showCaret = effBeat === TYPING_AT || effBeat === DROPDOWN_AT
  const klant = klantValue(effBeat)
  const activiteit = effBeat >= ACTIVITEIT_AT ? BOOKING_ACTIVITY : undefined
  const aantal = effBeat >= AANTAL_AT ? '8:00' : undefined
  const booked = effBeat >= BOOKED_AT
  const filled = effBeat >= FILLED_FROM || effBeat === REST_BEAT
  const flashing = effBeat === FLASH_AT

  return (
    <AppUiVisual label={t.screen.hours.addHours} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="hours-card" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.hours.title}
            detail={t.period.month}
            right={<HeaderStat value={filled ? TOTAL_BOOKED : TOTAL_BASE} label={t.word.hoursWord} />}
          />

          <div
            className="grid grid-cols-5 gap-1 px-3 pt-2 text-[8px] font-bold uppercase"
            style={{ color: 'var(--au-text2)', letterSpacing: '0.03em' }}
          >
            {WEEKDAYS.map((d) => (
              <span key={d} className="text-center">
                {d}
              </span>
            ))}
          </div>

          {/* Three weeks on a card with room for them, two on a phone: the last week is empty in
              every frame — the booking lands in the second — so it is the row that can go without
              taking any of the story with it. */}
          <div className={clsx(styles.calendar, 'grid flex-1 grid-cols-5 grid-rows-3 gap-1 px-3 py-2')}>
            {DAYS.map(([day, hours]) => {
              const isTarget = day === TARGET_DAY
              const isBooked = isTarget ? filled : Boolean(hours)
              const label = isTarget ? (filled ? '8:00' : null) : hours
              return (
                <div
                  key={day}
                  className={clsx(
                    'relative rounded-[5px] p-1',
                    styles.dayCell,
                    isTarget && ringed && styles.dayCellRing,
                    isTarget && flashing && styles.dayCellFlash,
                  )}
                  style={{ background: isBooked ? 'var(--au-accent-soft)' : 'var(--au-field)' }}
                >
                  <span className="text-[7.5px]" style={{ color: 'var(--au-text2)' }}>
                    {day}
                  </span>
                  {label && (
                    <span
                      className={clsx(
                        styles.dayHours,
                        isTarget && flashing && styles.dayHoursFlash,
                        'absolute right-1 bottom-1 text-[9px] font-bold',
                      )}
                      style={{ color: 'var(--au-accent)' }}
                    >
                      {label}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className={clsx(styles.scrim, modalOpen && styles.scrimVisible)} aria-hidden="true">
          <div
            className={clsx(styles.modal, 'w-[85%] max-w-[220px] rounded-[8px] border shadow-sm')}
            style={{ background: 'var(--au-surface)', borderColor: 'var(--au-border)' }}
          >
            <div className="border-b px-2.5 py-2" style={{ borderColor: 'var(--au-hairline)' }}>
              <div className="text-[10.5px] font-semibold" style={{ color: 'var(--au-text)' }}>
                {t.screen.hours.addHours}
              </div>
              <div className="text-[8px]" style={{ color: 'var(--au-text2)' }}>
                {fmt.weekdayDate(TARGET_DAY, weekdayIndexOf(TARGET_DAY))}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 px-2.5 py-2">
              <div className="flex min-w-0 flex-col gap-0.5">
                <FieldLabel>{t.word.client}</FieldLabel>
                <div
                  className={clsx(fieldClasses.box, klant && fieldClasses.filled)}
                  style={{ color: klant ? 'var(--au-text)' : 'var(--au-text2)' }}
                >
                  {klant || '—'}
                  {showCaret && (
                    <span aria-hidden style={{ color: 'var(--au-accent)' }}>
                      |
                    </span>
                  )}
                </div>
                {/* The same two names the day panel's lookup turns up first — real clients from the
                    base, not a placeholder invented to fill a second row. */}
                <div className={clsx(styles.dropdown, dropdownOpen && styles.dropdownVisible)}>
                  {LOOKUP.map((match) => (
                    <div
                      key={match.nr}
                      className={match.nr === HOURS_WEEK_BOOKING.nr ? styles.dropdownRowActive : styles.dropdownRow}
                    >
                      {match.name}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-1.5">
                <Field
                  label={t.word.activity}
                  value={activiteit ? t.yearActivity.jaarrekening.label : undefined}
                  placeholder="—"
                  className="flex-[1.4]"
                />
                <Field label={t.word.amount} value={aantal} placeholder="—" bold align="right" className="flex-1" />
              </div>
              <div className="flex justify-end pt-0.5">
                <span className={clsx(styles.button, booked && styles.buttonDone)}>
                  {booked ? t.screen.jaarplanning.booked : t.screen.hours.book}
                </span>
              </div>
            </div>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
